import React                     from 'react';
import View                      from '@widesk-ui/components/View';
import Segmented                 from '@widesk-ui/Segmented';
import Field                     from '@/TokamapEditor/ux/AreaSidebar/Field';
import ColorButton               from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import MixedSelect               from '@/TokamapEditor/ux/AreaSidebar/MixedSelect';
import { zoomAppearanceOptions } from '@/TokamapEditor/helpers/zoomAppearance';
import type { ShapeBadge }       from '@tokamap/core';
import type { ThemeLockRef }     from '@/TokamapEditor/helpers/themeImposed';

type Mixed<T> = T | undefined;

export type BadgeField = 'style' | 'size' | 'shape' | 'color' | 'anchor' | 'showText' | 'textPosition' | 'minZoomOffset';

export type BadgeValues = {
	style: Mixed<ShapeBadge['style']>;
	size: Mixed<ShapeBadge['size']>;
	shape: Mixed<ShapeBadge['shape']>;
	anchor: Mixed<ShapeBadge['anchor']>;
	showText: Mixed<ShapeBadge['showText']>;
	textPosition: Mixed<ShapeBadge['textPosition']>;
	color: Mixed<string>;
	colorMixed?: boolean;
	minZoomOffset: Mixed<number>;
	minZoomOffsetMixed?: boolean;
};

/** Encadre le contrôle d'un réglage du marqueur : un `Field` dans la sidebar d'une forme, un réglage imposable dans un thème. */
export type BadgeFieldFrame = (field: BadgeField, label: string, control: React.ReactNode, help?: string) => React.ReactNode;

const TEXT_DISPLAYS: Record<string, 'shown' | 'hover' | 'hidden'> = { true: 'shown', hover: 'hover', false: 'hidden' };

const fieldFrame = (locks: Partial<Record<BadgeField, ThemeLockRef | undefined>>): BadgeFieldFrame => (field, label, control, help) => (
	<Field key={field} label={label} help={help} lockedBy={locks[field]}>{control}</Field>
);

/**
 * Le choix « dans la forme / en marqueur » et les réglages du marqueur, partagés par la sidebar
 * d'une forme et l'éditeur de thème. `values` porte les valeurs communes à la sélection, `undefined`
 * quand elles diffèrent. Chaque valeur est rendue telle quelle, défauts compris : à l'appelant de
 * décider s'il les écrit.
 */
export default function BadgeSettings({ label, modes, mode, values, onMode, onChange, modeLockedBy, locks = {}, frame, standalone }: {
	label: React.ReactNode;
	modes: [inside: string, marker: string];
	mode: Mixed<'inside' | 'marker'>;
	values: BadgeValues;
	onMode: (mode: 'inside' | 'marker') => void;
	onChange: (patch: Partial<ShapeBadge>) => void;
	modeLockedBy?: ThemeLockRef;
	locks?: Partial<Record<BadgeField, ThemeLockRef | undefined>>;
	frame?: BadgeFieldFrame;
	standalone?: boolean;
}) {
	const framed = frame ?? fieldFrame(locks);

	return (
		<View gap="sm">
			{!standalone && (
				<Field label={label} lockedBy={modeLockedBy}>
					<Segmented
						block
						value={mode}
						onChange={value => onMode(value as 'inside' | 'marker')}
						options={[
							{ value: 'inside', label: modes[0] },
							{ value: 'marker', label: modes[1] },
						]}
					/>
				</Field>
			)}

			{mode === 'marker' && (
				<>
					{framed('style', 'Style du marqueur', (
						<Segmented
							block
							value={values.style}
							onChange={value => onChange({ style: value as ShapeBadge['style'] })}
							options={[
								{ value: 'disc', label: 'Pastille' },
								{ value: 'pin', label: 'Épingle' },
								{ value: 'tag', label: 'Étiquette' },
							]}
						/>
					))}

					{framed('size', 'Taille', (
						<Segmented
							block
							value={values.size}
							onChange={value => onChange({ size: value as ShapeBadge['size'] })}
							options={[
								{ value: 'small', label: 'Petit' },
								{ value: 'medium', label: 'Moyen' },
								{ value: 'large', label: 'Grand' },
							]}
						/>
					))}

					{framed('shape', 'Forme', (
						<Segmented
							block
							value={values.shape}
							onChange={value => onChange({ shape: value as ShapeBadge['shape'] })}
							options={[
								{ value: 'round', label: 'Rond' },
								{ value: 'square', label: 'Carré' },
							]}
						/>
					))}

					{framed('color', 'Couleur', (
						<ColorButton
							label="Marqueur"
							value={values.color}
							mixed={values.colorMixed}
							onChange={color => onChange({ color })}
						/>
					), 'Sans couleur choisie, celle du fond de la forme.')}

					{!standalone && framed('anchor', 'Ancrage', (
						<Segmented
							block
							value={values.anchor}
							onChange={value => onChange({ anchor: value as ShapeBadge['anchor'] })}
							options={[
								{ value: 'center', label: 'Centre de la forme' },
								{ value: 'top', label: 'Au-dessus' },
							]}
						/>
					))}

					{framed('showText', 'Affichage du texte', (
						<Segmented
							block
							value={values.showText === undefined ? undefined : TEXT_DISPLAYS[String(values.showText)]}
							onChange={value => onChange({ showText: value === 'hover' ? 'hover' : value === 'shown' })}
							options={[
								{ value: 'shown', label: 'Affiché' },
								{ value: 'hover', label: 'Au survol' },
								{ value: 'hidden', label: 'Masqué' },
							]}
						/>
					))}

					{values.style !== 'tag' && values.showText !== false && framed('textPosition', 'Position du texte', (
						<Segmented
							block
							value={values.textPosition}
							onChange={value => onChange({ textPosition: value as ShapeBadge['textPosition'] })}
							options={[
								{ value: 'above', label: 'Au-dessus' },
								{ value: 'below', label: 'En dessous' },
							]}
						/>
					))}

					{framed('minZoomOffset', 'Apparition au zoom', (
						<MixedSelect
							value={values.minZoomOffset ?? ''}
							mixed={values.minZoomOffsetMixed}
							options={zoomAppearanceOptions(values.minZoomOffset)}
							onChange={value => onChange({ minZoomOffset: typeof value === 'number' ? value : undefined })}
						/>
					), standalone
						? 'Le marqueur n\'apparaît qu\'une fois la carte zoomée d\'autant depuis sa vue de départ.'
						: 'Le marqueur n\'apparaît qu\'une fois la carte zoomée d\'autant depuis sa vue de départ. La forme, elle, reste affichée.')}
				</>
			)}
		</View>
	);
}
